import { useReducedMotion } from 'motion/react';
import type * as React from 'react';
import {
    type Action,
    type ExternalToast,
    Toaster as SonnerToaster,
    toast as sonnerToast,
} from 'sonner';

import { Icon } from '@gitbook/spine-icons';
import { HTTPError, throwIfNotExposable } from '@gitbook/util-debug';
import { useViewport } from '@gitbook/util-react-hooks';

import { LoadingIndicator } from '../Loading';
import { Portal } from '../Portal';
import { type IconDefinition, renderIcon } from '../util';
import styles from './Toaster.module.css';

/**
 * Distance Sonner keeps between the stack and the edges of the viewport: its `VIEWPORT_OFFSET`,
 * applied to every side.
 */
const VIEWPORT_OFFSET = 32;

/**
 * Width of the stack, and of every toast in it: Sonner's `TOAST_WIDTH`.
 */
const TOAST_WIDTH = 356;

/**
 * Kept between the stack and whatever it has moved aside for.
 */
const AVOID_GAP = 16;

/**
 * Width at which Sonner lays the stack out full-bleed, pinning it to both edges of the screen:
 * the `max-width` of the media query in its stylesheet.
 */
const SONNER_FULL_WIDTH_BREAKPOINT = 600;

/**
 * The transition Sonner's stylesheet gives the stack, with the slide it makes when moved aside
 * added. Restated whole because a transition is one property, and it goes inline for the reason
 * `right` does.
 */
const TOASTER_TRANSITION = 'transform 400ms ease, right 300ms ease-out';

/**
 * A stretch of the screen, left to right in viewport pixels. A `DOMRect` is one.
 */
export type ToasterObstacle = Pick<DOMRectReadOnly, 'left' | 'right'>;

export interface ToasterProps {
    /**
     * Something on screen the toasts must not cover: a panel open over the corner they stack in,
     * say. When it reaches into that corner, the toasts stack to its left instead — unless it
     * stops short of the corner, or nothing fits beside it, in which case they stay where they are.
     *
     * Ignored on a screen narrow enough for the toasts to span it, where there is no beside.
     */
    avoid?: ToasterObstacle | null;
}

/**
 * The single host for the toasts {@link toast} shows, stacked in the bottom-right corner of the
 * viewport. Mounted once, at the root of the application.
 */
export function Toaster(props: ToasterProps) {
    const { avoid } = props;
    const right = useToasterRightOffset(avoid);
    const prefersReducedMotion = useReducedMotion();

    return (
        <Portal>
            <SonnerToaster
                className={styles.toaster}
                // Inline rather than in the stylesheet: Sonner injects its own unlayered, and
                // nothing in a layer can take a property back from it.
                style={{
                    ...(right === null ? {} : { right }),
                    ...(prefersReducedMotion ? {} : { transition: TOASTER_TRANSITION }),
                }}
                toastOptions={{
                    classNames: {
                        toast: styles.toast,
                        content: styles.content,
                        icon: styles.icon,
                        description: styles.description,
                        closeButton: styles.closeButton,
                        actionButton: styles.actionButton,
                    },
                    closeButton: true,
                }}
                icons={{
                    success: <Icon.CheckCircleFill className="size-4 text-[var(--green-status)]" />,
                    error: <Icon.DangerCircleFill className="size-4 text-danger-low" />,
                    warning: <Icon.WarningCircleFill className="size-4 text-[var(--yellow-9)]" />,
                    loading: <LoadingIndicator />,
                }}
            />
        </Portal>
    );
}

export type ToastOptions = {
    icon?: IconDefinition | undefined;
    description?: React.ReactNode | undefined;
    action?: Action | React.ReactNode | undefined;
    id?: string | number | undefined;
    closeButton?: boolean | undefined;
    persist?: boolean | undefined;
    onDismiss?: ExternalToast['onDismiss'] | undefined;
    onAutoClose?: ExternalToast['onAutoClose'] | undefined;
};

type ToastSuccessOptions = Omit<ToastOptions, 'icon' | 'closeButton'>;
type ToastErrorOptions = Omit<ToastOptions, 'icon' | 'closeButton'>;
type ToastLoadingOptions = Omit<ToastOptions, 'icon' | 'closeButton' | 'duration'>;

type PromiseT<Data = any> = Promise<Data> | (() => Promise<Data>);
type PromiseTResult<Data = any> =
    | string
    | React.ReactNode
    | ((data: Data) => React.ReactNode | string | Promise<React.ReactNode | string>);
type PromiseExternalToast = Omit<ToastOptions, 'description'>;
export type ToastPromiseData<ToastData = any> = PromiseExternalToast & {
    loading?: string | React.ReactNode;
    success?: PromiseTResult<ToastData>;
    error?: PromiseTResult;
    description?: PromiseTResult;
    finally?: () => void | Promise<void>;
};

export type ToastCatchOptions = {
    /**
     * Callback to run when the promise is rejected.
     */
    onReject?: (error: unknown) => void;
};

/**
 * Show a toast.
 */
type Toast = ((message: React.ReactNode, options?: ToastOptions) => string | number) & {
    /**
     * Show a success toast.
     */
    success: (message: React.ReactNode, options?: ToastSuccessOptions) => string | number;

    /**
     * Show an error toast.
     * Consider using `exception` instead if wrapping an user action that can generate expected exposable errors.
     */
    error: (
        message: React.ReactNode,
        error: Error | null,
        data?: ToastErrorOptions
    ) => string | number;

    /**
     * Catch an error and show a toast if it happens.
     * If the error is critical, it will be thrown.
     */

    catch: <T>(
        fn: () => T | Promise<T>,
        message: React.ReactNode | ((error: Error) => void),
        options?: ToastCatchOptions
    ) => Promise<T | undefined>;

    /**
     * Show Promise-aware toast.
     */
    promise: <ToastData>(
        promise: PromiseT<ToastData>,
        data?: ToastPromiseData<ToastData>
    ) =>
        | (string & {
              unwrap: () => Promise<ToastData>;
          })
        | (number & {
              unwrap: () => Promise<ToastData>;
          })
        | {
              unwrap: () => Promise<ToastData>;
          };

    /**
     * Show a loading toast.
     */
    loading: (message: React.ReactNode, options?: ToastLoadingOptions) => string | number;

    /**
     * Show an info toast.
     */
    info: (message: React.ReactNode, options?: ToastOptions) => string | number;

    /**
     * Show a warning toast.
     * A less intense alternative to `error` — does not require an `Error` argument
     * and auto-dismisses like `info` / `success`.
     */
    warning: (message: React.ReactNode, options?: ToastOptions) => string | number;

    /**
     * Dismiss a toast by its id or all toasts if no id is provided.
     */
    dismiss: (id?: number | string) => string | number;
};

function adaptOptions(options?: ToastOptions): ExternalToast | undefined {
    if (!options) {
        return undefined;
    }

    return {
        ...options,
        icon: options.icon ? renderIcon(options.icon) : undefined,
        duration: options.persist ? Number.POSITIVE_INFINITY : options.action ? 8000 : undefined,
    };
}

function toast(message: React.ReactNode, options?: ToastOptions) {
    return sonnerToast(message, adaptOptions(options));
}

const success: Toast['success'] = (message, options) => {
    return sonnerToast.success(message, adaptOptions(options));
};

const error: Toast['error'] = (message, error, options) => {
    const description = (() => {
        if (options?.description !== undefined) {
            return options.description;
        }
        if (error instanceof HTTPError) {
            return error.expose ? error.message : undefined;
        }
        return undefined;
    })();

    const adaptedOptions = adaptOptions({
        ...options,
        persist: options?.persist ?? true,
        description,
    });

    return sonnerToast.error(message, adaptedOptions);
};

const catchFn: Toast['catch'] = async (fn, messageOrErrorHandler, options) => {
    try {
        return await fn();
    } catch (error: unknown) {
        if (error instanceof Error) {
            if (typeof messageOrErrorHandler === 'function') {
                messageOrErrorHandler(error);
            } else {
                toast.error(messageOrErrorHandler, error);
            }
        }
        options?.onReject?.(error);
        throwIfNotExposable(error);
    }
};

const promise: Toast['promise'] = (promise, options) => {
    return sonnerToast.promise(promise, {
        ...options,
        icon: options?.icon ? renderIcon(options?.icon) : undefined,
        error: (error: unknown) => {
            if (error instanceof HTTPError && error.expose) {
                return error.message;
            }
            return 'An unexpected error occurred';
        },
    });
};

const loading: Toast['loading'] = (message, options) => {
    return sonnerToast.loading(message, {
        ...adaptOptions(options),
        closeButton: false,
        duration: Number.POSITIVE_INFINITY,
    });
};

const info: Toast['info'] = (message, options) => {
    return sonnerToast.info(message, adaptOptions(options));
};

const warning: Toast['warning'] = (message, options) => {
    return sonnerToast.warning(message, adaptOptions(options));
};

toast.success = success;
toast.error = error;
toast.catch = catchFn;
toast.promise = promise;
toast.loading = loading;
toast.info = info;
toast.warning = warning;
toast.dismiss = sonnerToast.dismiss;

const typedToast: Toast = toast;

export { typedToast as toast };

/**
 * Where the stack has to go to keep clear of `avoid`, as its distance from the right edge of the
 * viewport — or null to leave it where Sonner puts it.
 */
function useToasterRightOffset(avoid: ToasterObstacle | null | undefined): number | null {
    const viewport = useViewport();
    return getToasterRightOffset({ viewportWidth: viewport.width, avoid });
}

/**
 * Distance from the right edge of the viewport that puts the stack `AVOID_GAP` left of `avoid`, or
 * null to leave it in its corner: when there is nothing to avoid, when `avoid` stops short of the
 * corner anyway, and when a stack beside it would run off the left of the screen — cut off being
 * worse than covered.
 */
function getToasterRightOffset(input: {
    viewportWidth: number;
    avoid: ToasterObstacle | null | undefined;
}): number | null {
    const { viewportWidth, avoid } = input;
    if (!avoid) {
        return null;
    }

    // A stack pinned to both edges of the screen has nowhere to go: it already spans everything
    // there is to move within, and a box given a left, a width and a right is over-constrained —
    // the browser drops the right, so moving it is not on offer in the first place.
    if (viewportWidth <= SONNER_FULL_WIDTH_BREAKPOINT) {
        return null;
    }

    const cornerLeft = viewportWidth - VIEWPORT_OFFSET - TOAST_WIDTH;
    if (avoid.right + AVOID_GAP <= cornerLeft) {
        return null;
    }

    const right = viewportWidth - avoid.left + AVOID_GAP;
    if (viewportWidth - right - TOAST_WIDTH < VIEWPORT_OFFSET) {
        return null;
    }
    return right;
}
